// src/components/notifications/NotificationsList.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import NotificationItem from './NotificationItem';

export default function NotificationsList({
  notifications,
  totalNotifications,
  selectedNotifications,
  loading,
  hasMore,
  onNotificationSelect,
  onNotificationClick,
  onNotificationAction,
  onLoadMore,
}) {
  const { t } = useTranslation();

  // Loading skeleton
  if (loading && notifications.length === 0) {
    return (
      <Stack spacing={2}>
        {[...Array(5)].map((_, index) => (
          <Card key={index}>
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  // Empty state
  if (notifications.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('notifications:messages.no_notifications')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {totalNotifications === 0
              ? t('notifications:list.no_notifications_yet')
              : t('notifications:list.no_notifications_match_filters')
            }
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Notifications list
  return (
    <Stack spacing={1}>
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.notification_id}
          notification={notification}
          isSelected={selectedNotifications.includes(notification.notification_id)}
          onSelect={onNotificationSelect}
          onClick={onNotificationClick}
          onActionClick={onNotificationAction}
        />
      ))}

      {/* Load More Button */}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? t('notifications:list.loading') : t('notifications:list.load_more')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}